<template>
  <div class="dashboard-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <span>加载中...</span>
    </div>

    <div v-if="!loading">
      <!-- 统计卡片区域 -->
      <div class="stats-grid">
        <!-- 总用户数卡片 -->
        <div class="stat-card">
          <div class="stat-icon user-icon">
            <i class="icon">👥</i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ formatNumber(stats.total_users) }}</div>
            <div class="stat-title">总用户数</div>
            <div class="stat-trend">
              今日新增: +{{ stats.new_users_today || 0 }}
            </div>
          </div>
        </div>

        <!-- 总菜品数卡片 -->
        <div class="stat-card">
          <div class="stat-icon dish-icon">
            <i class="icon">🍽️</i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ formatNumber(stats.total_dishes) }}</div>
            <div class="stat-title">总菜品数</div>
            <div class="stat-trend">
              今日新增: +{{ stats.new_dishes_today || 0 }}
            </div>
          </div>
        </div>

        <!-- 总订单数卡片 -->
        <div class="stat-card">
          <div class="stat-icon order-icon">
            <i class="icon">🛒</i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ formatNumber(stats.total_orders) }}</div>
            <div class="stat-title">总订单数</div>
            <div class="stat-trend">今日: {{ stats.orders_today || 0 }}</div>
          </div>
        </div>

        <!-- 模板中的签到卡片 -->
        <div class="stat-card">
          <div class="stat-icon signin-icon">
            <i class="icon">✅</i>
          </div>
          <div class="stat-info">
            <div class="stat-value">
              {{ formatNumber(stats.total_signin_users) }}
            </div>
            <div class="stat-title">总签到人数</div>
            <div class="stat-trend">
              今日签到: {{ stats.signin_users_today || 0 }} 人
            </div>
          </div>
        </div>
      </div>

      <!-- 图表区域 -->
      <div class="charts-grid">
        <!-- 用户注册趋势 -->
        <div class="chart-card">
          <div class="chart-header">
            <h3>近7天用户注册趋势</h3>
            <button class="refresh-btn" @click="refreshUserTrend">刷新</button>
          </div>
          <div ref="userTrendChart" class="chart-container"></div>
        </div>
        <!-- 菜品新增数量趋势图 -->
        <div class="chart-card">
          <div class="chart-header">
            <h3>近7天菜品新增趋势</h3>
            <button class="refresh-btn" @click="refreshDishTrend">刷新</button>
          </div>
          <div ref="dishTrendChart" class="chart-container"></div>
        </div>
        <!-- 订单趋势 -->
        <div class="chart-card">
          <div class="chart-header">
            <h3>近7天订单趋势</h3>
            <button class="refresh-btn" @click="refreshOrderTrend">刷新</button>
          </div>
          <div ref="orderTrendChart" class="chart-container"></div>
        </div>

        <!-- 打卡趋势 -->
        <div class="chart-card">
          <div class="chart-header">
            <h3>近7天打卡趋势</h3>
            <button class="refresh-btn" @click="refreshPunchTrend">刷新</button>
          </div>
          <div ref="punchTrendChart" class="chart-container"></div>
        </div>
      </div>

      <!-- 排行榜区域 -->
      <div  v-observe-visibility="visibilityChanged"  class="rankings-grid">
        <!-- 热门菜品排行（按订单量） -->
        <div class="rank-card">
          <div class="rank-header">
            <h3>🔥 热门菜品 Top 10</h3>
            <span class="tag tag-danger">按订单量</span>
          </div>
          <div class="rank-list">
            <div
              v-for="(dish, index) in hotDishesByOrder"
              :key="dish.id"
              class="rank-item"
            >
              <div :class="['rank-number', getRankClass(index)]">
                {{ index + 1 }}
              </div>
              <img :src="dish.image_url" class="rank-img" />
              <div class="rank-info">
                <div class="rank-name">{{ dish.name }}</div>
                <div class="rank-price">¥{{ dish.price }}</div>
              </div>
              <div class="rank-count">{{ dish.order_count }}单</div>
            </div>
            <div v-if="hotDishesByOrder.length === 0" class="empty-tip">
              暂无数据
            </div>
          </div>
        </div>

        <!-- 活跃用户排行 -->
        <div class="rank-card">
          <div class="rank-header">
            <h3>👑 活跃用户 Top 10</h3>
            <span class="tag tag-success">按上传菜品数</span>
          </div>
          <div class="rank-list">
            <div
              v-for="(user, index) in activeUsers"
              :key="user.openid"
              class="rank-item"
            >
              <div :class="['rank-number', getRankClass(index)]">
                {{ index + 1 }}
              </div>
              <img
                :src="user.avatar_url || defaultAvatar"
                class="rank-avatar"
              />
              <div class="rank-info">
                <div class="rank-name">
                  {{ user.nickname || "匿名用户" }}
                </div>
                <div class="rank-desc">上传了{{ user.dish_count }}道菜</div>
              </div>
              <button
                class="copy-openid-btn"
                @click="copyOpenId(user.openid)"
                title="点击复制 OpenID"
              >
                📋 复制 OpenID
              </button>
            </div>
            <div v-if="activeUsers.length === 0" class="empty-tip">
              暂无数据
            </div>
          </div>
        </div>

        <!-- 打卡达人排行 -->
        <div class="rank-card">
          <div class="rank-header">
            <h3>📅 打卡达人 Top 10</h3>
            <span class="tag tag-primary">按打卡次数</span>
          </div>
          <div class="rank-list">
            <div
              v-for="(user, index) in punchLeaders"
              :key="user.openid"
              class="rank-item"
            >
              <div :class="['rank-number', getRankClass(index)]">
                {{ index + 1 }}
              </div>
              <img
                :src="user.avatar_url || defaultAvatar"
                class="rank-avatar"
              />
              <div class="rank-info">
                <div class="rank-name">{{ user.nickname || "匿名用户" }}</div>
                <div class="rank-desc">打卡{{ user.punch_count }}次</div>
              </div>
              <button
                class="copy-openid-btn"
                @click="copyOpenId(user.openid)"
                title="点击复制 OpenID"
              >
                📋 复制 OpenID
              </button>
            </div>
            <div v-if="punchLeaders.length === 0" class="empty-tip">
              暂无数据
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getDashboardStats,
  getUserTrend,
  getOrderTrend,
  getSigninTrend, // 添加这个
  getHotDishesByOrder,
  getActiveUsers,
  getPunchLeaders,
  getDishTrend,
} from "@/api/houtai/dashboard.js";

import * as echarts from "echarts";

export default {
  name: "DashboardHome",
  data() {
    return {
      loading: true,
       // 加载控制标志
      chartsLoaded: false,
      chartsLoading: false,
      rankingsLoaded: false,
      rankingsLoading: false,
      // 统计数据
      stats: {
        total_users: 0,
        new_users_today: 0,
        total_dishes: 0,
        new_dishes_today: 0,
        total_orders: 0,
        orders_today: 0,
        pending_orders: 0,
        total_signin_users: 0, // 改：原 total_punch_users
        signin_users_today: 0, // 改：原 punch_users_today
        disabled_dishes: 0,
        low_recom_dishes: 0,
        no_category_dishes: 0,
        dishTrendChart: null, // 新增菜品趋势图实例
      },
      // 待办列表
      todoList: [],
      // 排行榜数据
      hotDishesByOrder: [],
      hotDishesByLike: [],
      activeUsers: [],
      punchLeaders: [],
      // 图表实例
      userTrendChart: null,
      orderTrendChart: null,
      punchTrendChart: null,
      orderStatusChart: null,
    };
  },
  created() {
    this.loadAllData();
  },
  beforeDestroy() {
    // 销毁图表实例
    if (this.userTrendChart) this.userTrendChart.dispose();
    if (this.orderTrendChart) this.orderTrendChart.dispose();
    if (this.punchTrendChart) this.punchTrendChart.dispose();
    if (this.dishTrendChart) this.dishTrendChart.dispose(); // 新增
    if (this.orderStatusChart) this.orderStatusChart.dispose();
  },
  mounted() {
    // 确保 DOM 完全渲染后再初始化图表
    setTimeout(() => {
      if (this.stats) {
        this.loadUserTrend();
        this.loadOrderTrend();
        this.loadSigninTrend();
        this.loadDishTrend();
      }
    }, 2500);
  },
  methods: {

 visibilityChanged(isVisible) {
    if (isVisible && !this.rankingsLoaded) {
      this.loadRankingsData(); // 调用统一的方法
    }
  },
 async loadRankingsData() {
    if (this.rankingsLoaded) return;
    
    try {
      this.rankingsLoading = true;
      // 并行加载所有排行榜数据
      await Promise.all([
        this.loadHotDishesByOrder(),
        this.loadActiveUsers(),
        this.loadPunchLeaders(),
      ]);
      this.rankingsLoaded = true;
    } catch (error) {
      console.error('加载排行榜失败:', error);
    } finally {
      this.rankingsLoading = false;
    }
  },
 // 加载所有数据
    async loadAllData() {
      this.loading = true;
      try {
        await Promise.all([
          // 1. 统计卡片数据
          this.loadStats(),
        
        ]);
      } catch (error) {
        console.error("加载数据失败:", error);
        alert("加载数据失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },

    // 显示提示消息
    showMessage(message) {
      // 创建提示元素
      const toast = document.createElement("div");
      toast.textContent = message;
      toast.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 9999;
      white-space: nowrap;
      animation: fadeInOut 1s ease;
    `;

      // 添加动画样式
      if (!document.querySelector("#toast-style")) {
        const style = document.createElement("style");
        style.id = "toast-style";
        style.textContent = `
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
          15% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          85% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        }
      `;
        document.head.appendChild(style);
      }

      document.body.appendChild(toast);

      // 1.5秒后移除
      setTimeout(() => {
        toast.remove();
      }, 1000);
    },

    async copyOpenId(openid) {
      try {
        await navigator.clipboard.writeText(openid);
        this.showMessage("✅ OpenID 已复制到剪贴板");
      } catch (err) {
        this.fallbackCopyOpenId(openid);
      }
    },

    fallbackCopyOpenId(openid) {
      const textarea = document.createElement("textarea");
      textarea.value = openid;
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      this.showMessage("✅ OpenID 已复制到剪贴板");
    },

   

    // 加载统计数据
    async loadStats() {
      try {
        const res = await getDashboardStats();
        if (res.code === 0) {
          this.stats = res.data;
          console.log("统计数据:", res.data);
        }
      } catch (error) {
        console.error("加载统计数据失败:", error);
      }
    },

    // 加载用户注册趋势
    async loadUserTrend() {
      try {
        const res = await getUserTrend({ days: 7 });
        if (res.code === 0 && res.data) {
          this.$nextTick(() => {
            this.initUserTrendChart(res.data);
          });
        }
      } catch (error) {
        console.error("加载用户趋势失败:", error);
      }
    },

    // 加载订单趋势
    async loadOrderTrend() {
      try {
        const res = await getOrderTrend({ days: 7 });
        if (res.code === 0 && res.data) {
          this.$nextTick(() => {
            this.initOrderTrendChart(res.data);
          });
        }
      } catch (error) {
        console.error("加载订单趋势失败:", error);
      }
    },

    // 加载签到趋势数据
    async loadSigninTrend() {
      try {
        const res = await getSigninTrend({ days: 7 });
        if (res.code === 0 && res.data) {
          this.$nextTick(() => {
            this.initSigninTrendChart(res.data);
          });
        }
      } catch (error) {
        console.error("加载签到趋势失败:", error);
      }
    },
    // 加载菜品新增趋势
    async loadDishTrend() {
      try {
        const res = await getDishTrend({ days: 7 });
        if (res.code === 0 && res.data) {
          this.$nextTick(() => {
            this.initDishTrendChart(res.data);
          });
        }
      } catch (error) {
        console.error("加载菜品新增趋势失败:", error);
      }
    },

    // 加载热门菜品（按订单量）
    async loadHotDishesByOrder() {
      try {
        const res = await getHotDishesByOrder({ limit: 10 });
        if (res.code === 0) {
          this.hotDishesByOrder = res.data || [];
        }
      } catch (error) {
        console.error("加载热门菜品失败:", error);
      }
    },

    // 加载活跃用户
    async loadActiveUsers() {
      try {
        const res = await getActiveUsers({ limit: 10 });
        if (res.code === 0) {
          this.activeUsers = res.data || [];
        }
      } catch (error) {
        console.error("加载活跃用户失败:", error);
      }
    },

    // 加载打卡达人
    async loadPunchLeaders() {
      try {
        const res = await getPunchLeaders({ limit: 10 });
        if (res.code === 0) {
          this.punchLeaders = res.data || [];
        }
      } catch (error) {
        console.error("加载打卡达人失败:", error);
      }
    },

    // 通用日期格式化方法
    formatDate(dateStr) {
      if (!dateStr) return "";
      // 处理 UTC 时间格式
      const date = new Date(dateStr);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`; // 返回 "4/13" 格式
    },

    // 或者返回 MM-DD 格式
    formatDateSimple(dateStr) {
      if (!dateStr) return "";
      // 将 UTC 时间转换为本地时间
      const date = new Date(dateStr);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${month}-${day}`; // 返回 "04-15"
    },

    // 初始化用户注册趋势图
    initUserTrendChart(data) {
      if (!this.$refs.userTrendChart) return;
      if (this.userTrendChart) this.userTrendChart.dispose();

      this.userTrendChart = echarts.init(this.$refs.userTrendChart);
      const dates = data.map((item) => this.formatDateSimple(item.date));
      const counts = data.map((item) => item.count);

      this.userTrendChart.setOption({
        tooltip: { trigger: "axis" },
        xAxis: { type: "category", data: dates, axisLabel: { rotate: 45 } },
        yAxis: { type: "value", name: "注册人数" },
        series: [
          {
            type: "line",
            data: counts,
            smooth: true,
            areaStyle: { opacity: 0.3 },
            lineStyle: { color: "#409EFF", width: 2 },
            symbol: "circle",
            symbolSize: 8,
          },
        ],
      });
    },

    // 初始化订单趋势图
    initOrderTrendChart(data) {
      if (!this.$refs.orderTrendChart) return;
      if (this.orderTrendChart) this.orderTrendChart.dispose();

      this.orderTrendChart = echarts.init(this.$refs.orderTrendChart);

      // 使用 formatDateSimple 格式化日期
      const dates = data.map((item) => this.formatDateSimple(item.date));
      const orderCounts = data.map((item) => item.order_count);
      const revenues = data.map((item) => item.revenue);

      this.orderTrendChart.setOption({
        tooltip: {
          trigger: "axis",
          formatter: function (params) {
            // 自定义 tooltip 显示完整信息
            let result = params[0].axisValue + "<br/>";
            params.forEach((param) => {
              result +=
                param.marker +
                " " +
                param.seriesName +
                ": " +
                param.value +
                "<br/>";
            });
            return result;
          },
        },
        legend: { data: ["订单数", "收入(元)"] },
        xAxis: { type: "category", data: dates, axisLabel: { rotate: 45 } },
        yAxis: [
          { type: "value", name: "订单数" },
          { type: "value", name: "收入(元)" },
        ],
        series: [
          {
            type: "bar",
            data: orderCounts,
            name: "订单数",
            yAxisIndex: 0,
            color: "#67C23A",
          },
          {
            type: "line",
            data: revenues,
            name: "收入(元)",
            yAxisIndex: 1,
            color: "#E6A23C",
            smooth: true,
          },
        ],
      });
    },

    // 初始化签到趋势图（原打卡趋势图）
    initSigninTrendChart(data) {
      if (!this.$refs.punchTrendChart) return;
      if (this.punchTrendChart) this.punchTrendChart.dispose();

      this.punchTrendChart = echarts.init(this.$refs.punchTrendChart);

      // 使用 formatDateSimple 格式化日期
      const dates = data.map((item) => this.formatDateSimple(item.date));
      // 注意：字段名从 punch_count 改为 signin_count
      const counts = data.map((item) => item.signin_count);

      this.punchTrendChart.setOption({
        tooltip: {
          trigger: "axis",
          formatter: function (params) {
            return (
              params[0].axisValue + "<br/>" + "签到人数: " + params[0].value
            );
          },
        },
        xAxis: { type: "category", data: dates, axisLabel: { rotate: 45 } },
        yAxis: { type: "value", name: "签到人数" },
        series: [
          {
            type: "bar",
            data: counts,
            color: "#909399",
            itemStyle: { borderRadius: [4, 4, 0, 0] },
          },
        ],
      });
    },

    // 初始化菜品新增趋势图
    initDishTrendChart(data) {
      console.log("初始化菜品趋势图, refs:", this.$refs.dishTrendChart); // 添加日志

      if (!this.$refs.dishTrendChart) {
        console.log("dishTrendChart DOM 元素不存在");
        return;
      }
      if (this.dishTrendChart) this.dishTrendChart.dispose();

      this.dishTrendChart = echarts.init(this.$refs.dishTrendChart);

      // 确保 data 是数组且有数据
      if (!data || data.length === 0) {
        console.log("菜品趋势数据为空");
        return;
      }

      // 格式化日期
      const dates = data.map((item) => this.formatDateSimple(item.date));
      const counts = data.map((item) => item.count);

      console.log("日期:", dates);
      console.log("数量:", counts);

      this.dishTrendChart.setOption({
        tooltip: {
          trigger: "axis",
          formatter: function (params) {
            return (
              params[0].axisValue +
              "<br/>" +
              "新增菜品: " +
              params[0].value +
              " 道"
            );
          },
        },
        xAxis: {
          type: "category",
          data: dates,
          axisLabel: { rotate: 45 },
        },
        yAxis: {
          type: "value",
          name: "新增菜品数量",
        },
        series: [
          {
            type: "bar",
            data: counts,
            name: "新增菜品",
            color: "#E6A23C",
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
            },
            label: {
              show: true,
              position: "top",
              color: "#E6A23C",
            },
          },
        ],
      });
    },

    // 刷新用户趋势图
    refreshUserTrend() {
      this.loadUserTrend();
    },

    // 刷新订单趋势图
    refreshOrderTrend() {
      this.loadOrderTrend();
    },

    // 刷新打卡趋势图
    refreshPunchTrend() {
      this.loadSigninTrend();
    },

    // 刷新菜品新增趋势
    refreshDishTrend() {
      this.loadDishTrend();
    },

    // 格式化数字（千分位）
    formatNumber(num) {
      if (num === undefined || num === null) return "0";
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // 获取排名样式类
    getRankClass(index) {
      if (index === 0) return "rank-1";
      if (index === 1) return "rank-2";
      if (index === 2) return "rank-3";
      return "";
    },
  },
};
</script>

<style scoped>
/* 复制按钮样式 */
.copy-openid-btn {
  background: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.copy-openid-btn:hover {
  background: #66b1ff;
  transform: translateY(-1px);
}

.copy-openid-btn:active {
  transform: translateY(0);
}

/* 响应式：小屏幕时按钮显示为图标 */
@media (max-width: 768px) {
  .copy-openid-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}

.dashboard-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  gap: 10px;
  font-size: 16px;
  color: #409eff;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e8f4fd;
  border-top-color: #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 统计卡片区域 */
.stats-grid {
  display: grid;
  gap: 20px;
  margin-bottom: 24px;
}

/* 大屏幕：一排4个 */
@media (min-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* 中等屏幕：一排3个 */
@media (min-width: 992px) and (max-width: 1199px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* 平板：一排2个 */
@media (min-width: 576px) and (max-width: 991px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* 手机：一排1个
@media (max-width: 575px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
} */

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 54px;
  height: 54px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.user-icon {
  background: #e8f4fd;
}
.dish-icon {
  background: #fdf6ec;
}
.order-icon {
  background: #e8f8f2;
}
.revenue-icon {
  background: #feecec;
}
.kitchen-icon {
  background: #f4e8fe;
}
.punch-icon {
  background: #e8fdf5;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

.stat-title {
  font-size: 14px;
  color: #909399;
  margin-top: 4px;
}

.stat-trend {
  font-size: 12px;
  color: #67c23a;
  margin-top: 6px;
}

/* 待办区域 */
.todo-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
}

.badge {
  background: #f56c6c;
  color: white;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 12px;
  line-height: 20px;
}

.todo-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.todo-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
}

.todo-item.warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.todo-item.danger {
  background: #feecec;
  color: #f56c6c;
}

.todo-item.info {
  background: #e8f4fd;
  color: #409eff;
}

.todo-count {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 0 6px;
  margin-left: 4px;
}

/* 图表区域 */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

@media (max-width: 1100px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.chart-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.refresh-btn {
  background: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.refresh-btn:hover {
  opacity: 0.8;
}

.chart-container {
  height: 320px;
  width: 100%;
}

/* 排行榜区域 */
.rankings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
}

@media (max-width: 900px) {
  .rankings-grid {
    grid-template-columns: 1fr;
  }
}

.rank-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.rank-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.rank-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.tag-danger {
  background: #fef0e0;
  color: #e6a23c;
}

.tag-warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.tag-success {
  background: #e8f8f2;
  color: #67c23a;
}

.tag-primary {
  background: #e8f4fd;
  color: #409eff;
}

.rank-list {
  max-height: 480px;
  overflow-y: auto;
}

.rank-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.rank-item:last-child {
  border-bottom: none;
}

.rank-number {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  background: #f5f7fa;
  color: #909399;
}

.rank-number.rank-1 {
  background: #fef0e0;
  color: #e6a23c;
}

.rank-number.rank-2 {
  background: #f0f9eb;
  color: #67c23a;
}

.rank-number.rank-3 {
  background: #e8f4fd;
  color: #409eff;
}

.rank-img {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
}

.rank-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.rank-info {
  flex: 1;
}

.rank-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.rank-price,
.rank-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.rank-count {
  font-size: 14px;
  font-weight: bold;
  color: #e6a23c;
}

.empty-tip {
  text-align: center;
  padding: 40px;
  color: #909399;
}
</style>